import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import healthService from '../../services/healthService';
import studentsService from '../../services/studentsService';
import DialogModal from '../../components/DialogModal';
import { MagnifyingGlass, Plus, PencilSimple, Trash, Bell, CheckCircle } from '@phosphor-icons/react';

const INCIDENT_TYPES = ['injury', 'illness', 'accident', 'allergic_reaction'];
const SEVERITIES = ['low', 'medium', 'high', 'critical'];
const STATUSES = ['open', 'resolved'];

const emptyForm = {
  student_id: '',
  incident_date: new Date().toISOString().split('T')[0],
  incident_type: 'injury',
  severity: 'low',
  description: '',
  witnesses: '',
  parent_notified: false,
  follow_up_required: false,
  follow_up_notes: '',
};

const severityColor = (severity) => {
  switch (severity) {
    case 'critical': return { background: '#fee2e2', color: '#991b1b' };
    case 'high': return { background: '#ffedd5', color: '#c2410c' };
    case 'medium': return { background: '#fef9c3', color: '#a16207' };
    default: return { background: '#f0fdf4', color: '#16a34a' };
  }
};

function HealthIncidentsPage() {
  const [loading, setLoading] = useState(true);
  const [incidents, setIncidents] = useState([]);
  const [filteredIncidents, setFilteredIncidents] = useState([]);
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [notifyTarget, setNotifyTarget] = useState(null);
  const [resolveTarget, setResolveTarget] = useState(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredIncidents(incidents);
      return;
    }
    const term = searchTerm.toLowerCase();
    setFilteredIncidents(
      incidents.filter(
        (i) => getStudentName(i.student_id).toLowerCase().includes(term) || i.description.toLowerCase().includes(term)
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidents, searchTerm, students]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [incidentsData, studentsData] = await Promise.all([
        healthService.listIncidents(statusFilter ? { status: statusFilter } : {}),
        studentsService.listStudents().catch(() => []),
      ]);
      setIncidents(Array.isArray(incidentsData) ? incidentsData : []);
      setStudents(Array.isArray(studentsData) ? studentsData : studentsData?.items || studentsData?.students || []);
    } catch (error) {
      toast.error('Failed to load health incidents');
    } finally {
      setLoading(false);
    }
  };

  const getStudentName = (studentId) => {
    const student = students.find((s) => s.student_id === studentId);
    return student ? `${student.first_name} ${student.last_name}` : studentId;
  };

  const openCreateModal = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (incident) => {
    setEditingId(incident.id);
    setFormData({
      student_id: incident.student_id,
      incident_date: incident.incident_date,
      incident_type: incident.incident_type,
      severity: incident.severity,
      description: incident.description,
      witnesses: incident.witnesses || '',
      parent_notified: incident.parent_notified,
      follow_up_required: incident.follow_up_required,
      follow_up_notes: incident.follow_up_notes || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.student_id || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      if (editingId) {
        const { student_id, ...rest } = formData;
        await healthService.updateIncident(editingId, rest);
        toast.success('Health incident updated');
      } else {
        await healthService.createIncident(formData);
        toast.success('Health incident reported');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to save health incident');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await healthService.deleteIncident(deleteTarget);
      toast.success('Health incident deleted');
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete health incident');
    } finally {
      setDeleteLoading(false);
    }
  };

  const confirmNotifyParent = async () => {
    if (!notifyTarget) return;
    try {
      await healthService.updateIncident(notifyTarget, { parent_notified: true });
      toast.success('Marked as parent notified');
      loadData();
    } catch (error) {
      toast.error('Failed to update notification status');
    } finally {
      setNotifyTarget(null);
    }
  };

  const confirmResolve = async () => {
    if (!resolveTarget) return;
    try {
      await healthService.updateIncident(resolveTarget, { status: 'resolved' });
      toast.success('Incident marked resolved');
      loadData();
    } catch (error) {
      toast.error('Failed to resolve incident');
    } finally {
      setResolveTarget(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading health incidents...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Health — Incident Reports</h1>
            <p>Injuries, illnesses, accidents, and allergic reactions</p>
          </div>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <Plus size={20} />
            Report Incident
          </button>
        </div>

        <div className="filters-bar">
          <div className="search-input">
            <MagnifyingGlass size={20} />
            <input
              type="text"
              placeholder="Search by student or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
        </div>

        <div className="table-container">
          {filteredIncidents.length === 0 ? (
            <div className="empty-state">
              <p>No health incidents found</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Severity</th>
                  <th>Description</th>
                  <th>Parent Notified</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredIncidents.map((incident) => (
                  <tr key={incident.id}>
                    <td>{getStudentName(incident.student_id)}</td>
                    <td>{incident.incident_date}</td>
                    <td className="text-capitalize">{incident.incident_type.replace('_', ' ')}</td>
                    <td>
                      <span className="badge" style={severityColor(incident.severity)}>
                        {incident.severity}
                      </span>
                    </td>
                    <td>{incident.description}</td>
                    <td>{incident.parent_notified ? 'Yes' : 'No'}</td>
                    <td>
                      {incident.status === 'open' ? (
                        <span className="badge inactive">Open</span>
                      ) : (
                        <span className="badge active">Resolved</span>
                      )}
                    </td>
                    <td className="action-buttons">
                      {!incident.parent_notified && (
                        <button className="btn-icon" title="Mark parent notified" onClick={() => setNotifyTarget(incident.id)}>
                          <Bell size={18} />
                        </button>
                      )}
                      {incident.status === 'open' && (
                        <button className="btn-icon" title="Mark resolved" onClick={() => setResolveTarget(incident.id)}>
                          <CheckCircle size={18} color="#16a34a" />
                        </button>
                      )}
                      <button className="btn-icon" title="Edit" onClick={() => openEditModal(incident)}>
                        <PencilSimple size={18} />
                      </button>
                      <button
                        className="btn-icon btn-danger"
                        title="Delete"
                        onClick={() => setDeleteTarget(incident.id)}
                      >
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingId ? 'Edit Health Incident' : 'Report Health Incident'}</h2>
              <button type="button" className="btn-close" onClick={() => setShowModal(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleSubmit}>
              {!editingId && (
                <div className="form-group">
                  <label>Student *</label>
                  <select value={formData.student_id} onChange={(e) => setFormData({ ...formData, student_id: e.target.value })} required>
                    <option value="">Select Student</option>
                    {students.map((s) => (
                      <option key={s.id} value={s.student_id}>{s.first_name} {s.last_name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Incident Date *</label>
                  <input type="date" value={formData.incident_date} onChange={(e) => setFormData({ ...formData, incident_date: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Type *</label>
                  <select value={formData.incident_type} onChange={(e) => setFormData({ ...formData, incident_type: e.target.value })} required>
                    {INCIDENT_TYPES.map((t) => (
                      <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1).replace('_', ' ')}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Severity *</label>
                <select value={formData.severity} onChange={(e) => setFormData({ ...formData, severity: e.target.value })} required>
                  {SEVERITIES.map((s) => (
                    <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required />
              </div>

              <div className="form-group">
                <label>Witnesses</label>
                <input type="text" value={formData.witnesses} onChange={(e) => setFormData({ ...formData, witnesses: e.target.value })} />
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.parent_notified}
                    onChange={(e) => setFormData({ ...formData, parent_notified: e.target.checked })}
                    style={{ width: 'auto', marginRight: 8 }}
                  />
                  Parent already notified
                </label>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.follow_up_required}
                    onChange={(e) => setFormData({ ...formData, follow_up_required: e.target.checked })}
                    style={{ width: 'auto', marginRight: 8 }}
                  />
                  Follow-up required
                </label>
              </div>

              {formData.follow_up_required && (
                <div className="form-group">
                  <label>Follow-up Notes</label>
                  <textarea value={formData.follow_up_notes} onChange={(e) => setFormData({ ...formData, follow_up_notes: e.target.value })} />
                </div>
              )}

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : editingId ? 'Update' : 'Report Incident'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DialogModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Health Incident"
        message="This will permanently delete this health incident record. Continue?"
        type="warning"
        isLoading={deleteLoading}
        primaryAction={{ label: 'Delete', onClick: confirmDelete, loadingLabel: 'Deleting...' }}
        secondaryAction={{ label: 'Cancel' }}
      />

      <DialogModal
        isOpen={!!notifyTarget}
        onClose={() => setNotifyTarget(null)}
        title="Mark Parent Notified"
        message="Confirm that the parent/guardian has been notified about this incident?"
        type="info"
        primaryAction={{ label: 'Confirm', onClick: confirmNotifyParent }}
        secondaryAction={{ label: 'Cancel' }}
      />

      <DialogModal
        isOpen={!!resolveTarget}
        onClose={() => setResolveTarget(null)}
        title="Mark Incident Resolved"
        message="Mark this health incident as resolved?"
        type="success"
        primaryAction={{ label: 'Mark Resolved', onClick: confirmResolve }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </Layout>
  );
}

export default HealthIncidentsPage;
